import type { ReactNode } from "react";

/**
 * The three glyphs the console needs, as paths rather than characters.
 *
 * `⏸` and `■` are real Unicode and render beautifully — on a machine with a
 * font that has them. On the clinic tablets, and in a bare Linux browser, they
 * come out as tofu boxes beside a perfectly good label. A path always draws.
 *
 * `aria-hidden` on all of them: every one of these sits next to a word that
 * already says what the button does, so announcing them again would only make
 * a screen reader slower.
 */
export function PlayIcon() {
  return (
    <svg className="btn-icon" viewBox="0 0 16 16" aria-hidden="true" focusable="false">
      <path d="M4 2.5v11a.5.5 0 0 0 .77.42l8.5-5.5a.5.5 0 0 0 0-.84l-8.5-5.5A.5.5 0 0 0 4 2.5Z" />
    </svg>
  );
}

export function PauseIcon() {
  return (
    <svg className="btn-icon" viewBox="0 0 16 16" aria-hidden="true" focusable="false">
      <rect x="3.5" y="2.5" width="3.5" height="11" rx="1" />
      <rect x="9" y="2.5" width="3.5" height="11" rx="1" />
    </svg>
  );
}

export function StopIcon() {
  return (
    <svg className="btn-icon" viewBox="0 0 16 16" aria-hidden="true" focusable="false">
      <rect x="3" y="3" width="10" height="10" rx="1.5" />
    </svg>
  );
}

/** The delay broadcast. Was a 📢 emoji, which arrives in whatever colour and
 *  weight the platform's emoji font feels like — beside three monochrome
 *  icons that inherit the button's own colour, it read as a sticker. */
export function MegaphoneIcon() {
  return (
    <svg className="btn-icon" viewBox="0 0 16 16" aria-hidden="true" focusable="false">
      <path d="M13.4 1.6a.75.75 0 0 0-.79.06L7.3 5.5H4.25A2.25 2.25 0 0 0 2 7.75v.5a2.25 2.25 0 0 0 1.85 2.21l.9 3.3a1 1 0 0 0 .97.74h.9a1 1 0 0 0 .96-1.27l-.75-2.73h.47l5.31 3.84a.75.75 0 0 0 1.19-.61V2.27a.75.75 0 0 0-.4-.67Z" />
    </svg>
  );
}

/* --- Navigation ------------------------------------------------------------
 *
 * One flat set, drawn on the same 16-unit grid as the four above and inheriting
 * `currentColor`, so a rail of six reads as one family rather than six borrowed
 * glyphs. `nav-icon` rather than `btn-icon`: these sit beside a label at rest
 * rather than inside a button, and want their own size.
 */

function NavIcon({ children }: { children: ReactNode }) {
  return (
    <svg className="nav-icon" viewBox="0 0 16 16" aria-hidden="true" focusable="false">
      {children}
    </svg>
  );
}

export function DashboardIcon() {
  return (
    <NavIcon>
      <rect x="2" y="2" width="5.5" height="6.5" rx="1.2" />
      <rect x="8.5" y="2" width="5.5" height="4" rx="1.2" />
      <rect x="2" y="9.5" width="5.5" height="4.5" rx="1.2" />
      <rect x="8.5" y="7" width="5.5" height="7" rx="1.2" />
    </NavIcon>
  );
}

export function SessionsIcon() {
  return (
    <NavIcon>
      <rect x="2" y="3" width="12" height="11" rx="1.8" fill="none" stroke="currentColor" strokeWidth="1.4" />
      <path d="M2 6.2h12" stroke="currentColor" strokeWidth="1.4" />
      <path d="M5 1.6v2.4M11 1.6v2.4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </NavIcon>
  );
}

export function PatientsIcon() {
  return (
    <NavIcon>
      <circle cx="6" cy="5" r="2.6" />
      <path d="M1.6 13.4c0-2.3 2-4 4.4-4s4.4 1.7 4.4 4a.6.6 0 0 1-.6.6H2.2a.6.6 0 0 1-.6-.6Z" />
      <path d="M11.2 5.2a2 2 0 1 1 .9 3.8" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
      <path d="M11.8 10.2c1.6.3 2.7 1.5 2.7 3.2a.6.6 0 0 1-.6.6h-1.6" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
    </NavIcon>
  );
}

export function BoardIcon() {
  return (
    <NavIcon>
      <rect x="1.5" y="2.5" width="13" height="9" rx="1.6" fill="none" stroke="currentColor" strokeWidth="1.4" />
      <path d="M5.5 14h5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
      <path d="M8 11.5V14" stroke="currentColor" strokeWidth="1.4" />
    </NavIcon>
  );
}

export function ChamberIcon() {
  return (
    <NavIcon>
      <path d="M8 1.8 2.2 5.1v8.1c0 .4.3.8.8.8h10c.4 0 .8-.4.8-.8V5.1L8 1.8Z" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
      <path d="M8 6.4v3.8M6.1 8.3h3.8" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </NavIcon>
  );
}

export function AdminIcon() {
  return (
    <NavIcon>
      <circle cx="8" cy="8" r="2.3" fill="none" stroke="currentColor" strokeWidth="1.4" />
      <path d="M8 1.6l1.3 1.5 1.9-.5.5 1.9 1.9.6-.9 1.8.9 1.8-1.9.6-.5 1.9-1.9-.5L8 12.6l-1.3-1.5-1.9.5-.5-1.9-1.9-.6.9-1.8-.9-1.8 1.9-.6.5-1.9 1.9.5L8 1.6Z" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round" />
    </NavIcon>
  );
}

export function MenuIcon() {
  return (
    <NavIcon>
      <path d="M2.5 4h11M2.5 8h11M2.5 12h11" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </NavIcon>
  );
}

export function BackIcon() {
  return (
    <NavIcon>
      <path d="M9.8 3.2 5 8l4.8 4.8" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </NavIcon>
  );
}

export function CalendarIcon() {
  return (
    <NavIcon>
      <rect x="2" y="3" width="12" height="11" rx="1.8" fill="none" stroke="currentColor" strokeWidth="1.4" />
      <path d="M2 6.2h12" stroke="currentColor" strokeWidth="1.4" />
      <path d="M5 1.6v2.4M11 1.6v2.4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
      {/* The days, which is what tells this apart from the sessions glyph at
          16 pixels — one is a day sheet, this is a month of them. */}
      <rect x="4.4" y="8.4" width="2" height="2" rx="0.5" />
      <rect x="7.5" y="8.4" width="2" height="2" rx="0.5" />
      <rect x="10.6" y="8.4" width="2" height="2" rx="0.5" />
      <rect x="4.4" y="11.2" width="2" height="2" rx="0.5" />
      <rect x="7.5" y="11.2" width="2" height="2" rx="0.5" />
    </NavIcon>
  );
}
